/**
 * invitation-row — one organization the signed-in person is invited into,
 * and the two answers it takes. The identity component of a
 * `users/{uid}/notifications/{token}` row of kind `invitation`, mounted by
 * the organization entry and the organization chooser alike.
 *
 * The question the row answers is "do I join this organization?", so it
 * carries what a person needs for that and nothing the lines above or the
 * controls already say: the organization, who asked and as what, and the one
 * cost of saying yes — members will see the person's name and address.
 *
 * Decline is confirmed in place: nothing un-declines, only the inviter can
 * invite again, and the control sits beside Accept. The confirm swaps the
 * action pair and the advisory line in their own slots, so nothing under the
 * pointer moves. Every outcome answers at the row: the in-flight mark sits on
 * the control pressed, a refusal replaces the row's sentence with the
 * server's verdict and its remedy, a failure carrying no verdict hands the
 * controls back. An accepted or declined row leaves with the server's own
 * deletion — the accepted organization opens, which is the answer.
 *
 * A founding invitation — the owner invitation a staff approval founded the
 * organization with — is the operator's offer, and accepting it concludes the
 * founder's contract with the operator. Its row carries the operator's
 * platform terms as the consent the sign-in step carries (sign-consent): a
 * checkbox whose label links the terms, and a line linking the privacy
 * notice, both opened in place from `platformLegalState` — each href the
 * bare host's own `/legal`, where those texts stand. Accept stays
 * unpressable until the box is ticked, and carries the versions shown; the
 * server refuses an accept of that invitation without them.
 */
import { SchmancyElement, schmancyElement } from "@mhmo91/schmancy/mixins";
import { show } from "@mhmo91/schmancy/overlay";
import type { LegalDocument } from "@momo/i18n";
import type { AcceptInvitation } from "@momo/types";
import { html, nothing } from "lit";
import "@mhmo91/schmancy/visually-hidden";
import { property, state } from "lit/decorators.js";
import { choose } from "lit/directives/choose.js";
import { when } from "lit/directives/when.js";
import { filter, take, takeUntil } from "rxjs";
import { LocalizeMixin } from "../i18n/localize.ts";
import "../legal/legal-document.view.ts";
import {
	isPlainLeftClick,
	legalDocumentFor,
	legalRetry$,
	platformLegalState,
} from "../legal/legal.state.ts";
import {
	type InvitationAction,
	invitationAction$,
	invitationByOrganization,
	invitationResult$,
} from "./invitation.state.ts";

/** Where this row's own act stands. Null is a row nobody has touched. */
type RowAnswer =
	| { readonly kind: "accepting" }
	| { readonly kind: "declining" }
	| {
			readonly kind: "refused";
			readonly refusal: AcceptInvitation.InvitationRefusal;
	  }
	/** The call came back a failure carrying no verdict, so the act did not
	 *  take and pressing again can still land it. */
	| { readonly kind: "failed" };

@schmancyElement("invitation-row")
export class InvitationRow extends LocalizeMixin(SchmancyElement) {
	/** The organization this row offers — what both callables address. */
	@property({ type: String }) orgId = "";

	@state() private confirming = false;
	@state() private answer: RowAnswer | null = null;
	/** The versions the box was ticked against. A republished text unticks
	 *  it, so Accept never carries a version the person did not agree to. */
	@state() private platformTermsAgreed: InvitationAction["platformTerms"] =
		null;

	private openLegalDocument(
		document: LegalDocument,
		identityUnavailable: boolean,
	) {
		show(
			() => html`
				<legal-document
					.document=${document}
					?identityUnavailable=${identityUnavailable}
				></legal-document>
			`,
		)
			.pipe(takeUntil(this.disconnecting))
			.subscribe();
	}

	private answerInvitation(
		kind: "accept" | "decline",
		platformTerms?: InvitationAction["platformTerms"],
	) {
		if (this.answer?.kind === "accepting" || this.answer?.kind === "declining")
			return;
		this.answer = { kind: kind === "accept" ? "accepting" : "declining" };
		const requestId = crypto.randomUUID();
		invitationResult$
			.pipe(
				filter((result) => result.requestId === requestId),
				take(1),
				takeUntil(this.disconnecting),
			)
			.subscribe((result) => {
				// An ok answer deletes the row server-side; nothing is left to say
				// here and the listener drops this element.
				if (result.ok) return;
				this.answer =
					result.refusal !== undefined
						? { kind: "refused", refusal: result.refusal }
						: { kind: "failed" };
			});
		invitationAction$.next({
			kind,
			requestId,
			orgId: this.orgId,
			platformTerms,
		});
	}

	override render() {
		const row = invitationByOrganization.get().get(this.orgId);
		if (row === undefined) return nothing;
		const { workspaceName, roleName, invitedBy } = row;
		const answer = this.answer;
		// The operator's texts, for a founding invitation alone. Resolved once
		// here, so the document opened and the version Accept carries are the
		// same text. The operator's brand and domain are the host the founder
		// is on.
		const platform = platformLegalState.value;
		const unavailable = this.t("legal.identityUnavailable");
		const platformTerms =
			row.founding === true && platform.settled
				? legalDocumentFor(
						"terms",
						this.uiLanguage,
						window.location.host,
						unavailable,
						platform,
					)
				: null;
		const platformPrivacy =
			row.founding === true && platform.settled
				? legalDocumentFor(
						"privacy",
						this.uiLanguage,
						window.location.host,
						unavailable,
						platform,
					)
				: null;
		const termsDoc = platformTerms?.document ?? null;
		const privacyDoc = platformPrivacy?.document ?? null;
		const agreed = this.platformTermsAgreed;
		const platformTermsChecked =
			termsDoc !== null &&
			privacyDoc !== null &&
			agreed?.tosVersion === termsDoc.version &&
			agreed.privacyVersion === privacyDoc.version;
		const acceptable = row.founding !== true || platformTermsChecked;
		// Which pair of controls stands. A failure hands the choice back, so it
		// reads the person's own confirm position rather than the act that did
		// not settle.
		const arm =
			answer === null || answer.kind === "failed"
				? this.confirming
					? "confirming"
					: "idle"
				: answer.kind === "accepting"
					? "idle"
					: answer.kind === "declining"
						? "confirming"
						: "settled";

		return html`
			<schmancy-surface type="containerLow" class="block rounded-2xl">
				<div class="grid gap-3 p-4">
					<div class="grid gap-1">
						<schmancy-typography type="title" token="sm">${workspaceName}</schmancy-typography>
						<schmancy-typography
							type="body"
							token="sm"
							role=${answer?.kind === "refused" ? "alert" : nothing}
							class=${this.classMap({
								"text-error-default": answer?.kind === "refused",
								"text-surface-onVariant": answer?.kind !== "refused",
							})}
						>
							${
								answer?.kind === "refused"
									? this.t(`admin.invitations.refused.${answer.refusal}.said`)
									: this.t("admin.invitations.by", { invitedBy, roleName })
							}
						</schmancy-typography>
						<!-- One slot, three tenants: the cost of yes, the cost of no, the
						     way back from a verdict. Two label/xs lines (28px) held while
						     controls stand — the German cost line takes two at 390 — so
						     the buttons under the pointer never move; a settled row holds
						     nothing below it. -->
						<schmancy-typography
							type="label"
							token="xs"
							class=${this.classMap({
								"line-clamp-2 text-surface-onVariant": true,
								"min-h-7": arm !== "settled",
							})}
						>
							${
								answer?.kind === "refused"
									? this.t(
											`admin.invitations.refused.${answer.refusal}.remedy`,
											{ invitedBy },
										)
									: answer?.kind === "failed"
										? this.t("admin.invitations.failed")
										: arm === "confirming"
											? this.t("admin.invitations.declineCost", { invitedBy })
											: this.t("admin.invitations.cost")
							}
						</schmancy-typography>
					</div>

					${when(row.founding === true && arm !== "settled", () =>
						!platform.settled
							? html`
									<div class="grid min-h-12 items-center">
										<schmancy-spinner size="sm" aria-hidden="true"></schmancy-spinner>
										<schmancy-visually-hidden role="status" aria-live="polite"
											>${this.t("auth.signConsent.loadingTerms")}</schmancy-visually-hidden
										>
									</div>
								`
							: platform.operator === "absent"
								? html`<schmancy-typography role="alert" type="body" token="sm" class="text-error-default">${this.t("auth.signConsent.operatorMissing")}</schmancy-typography>`
								: termsDoc === null || privacyDoc === null
									? html`
											<div class="flex flex-wrap items-center gap-2">
												<schmancy-typography role="alert" type="body" token="sm" class="text-error-default">${this.t("auth.signConsent.termsUnavailable")}</schmancy-typography>
												<schmancy-button
													data-terms-retry
													variant="text"
													size="sm"
													@click=${() => legalRetry$.next()}
													>${this.t("auth.signConsent.retry")}</schmancy-button
												>
											</div>
										`
									: html`
											<div class="grid gap-2">
												<schmancy-checkbox
													data-platform-terms
													.value=${platformTermsChecked}
													?disabled=${answer?.kind === "accepting" || answer?.kind === "declining"}
													@change=${(e: CustomEvent<{ value: boolean }>) => {
														this.platformTermsAgreed = e.detail?.value
															? {
																	tosVersion: termsDoc.version,
																	privacyVersion: privacyDoc.version,
																}
															: null;
													}}
												>
													<schmancy-typography type="body" token="sm">
														${this.t("auth.signConsent.agreeToThe")}
														<a
															href="/legal#terms"
															class="font-semibold text-primary-default hover:underline"
															@click=${(e: MouseEvent) => {
																e.stopPropagation();
																if (!isPlainLeftClick(e)) return;
																e.preventDefault();
																this.openLegalDocument(
																	termsDoc,
																	platformTerms?.identityUnavailable ?? false,
																);
															}}
														>${this.t("admin.invitations.platformTerms")}</a>.
													</schmancy-typography>
												</schmancy-checkbox>
												<schmancy-typography type="body" token="sm">
													${this.t("auth.signConsent.privacyNotice")}
													<a
														href="/legal#privacy"
														class="font-semibold text-primary-default hover:underline"
														@click=${(e: MouseEvent) => {
															if (!isPlainLeftClick(e)) return;
															e.preventDefault();
															this.openLegalDocument(
																privacyDoc,
																platformPrivacy?.identityUnavailable ?? false,
															);
														}}
													>${this.t("admin.invitations.platformPrivacy")}</a>.
												</schmancy-typography>
											</div>
										`,
					)}

					${choose(arm, [
						[
							"idle",
							() => html`
								<div class="flex flex-wrap items-center gap-2">
									<schmancy-button
										data-accept
										variant="filled"
										size="sm"
										?busy=${answer?.kind === "accepting"}
										?disabled=${!acceptable}
										.ariaLabel=${this.t("admin.invitations.acceptAria", { name: workspaceName })}
										@click=${() => {
											if (!acceptable) return;
											this.answerInvitation(
												"accept",
												row.founding === true ? agreed : undefined,
											);
										}}
										>${this.t("admin.invitations.accept")}</schmancy-button
									>
									<schmancy-button
										data-decline
										variant="outlined"
										color="neutral"
										size="sm"
										?disabled=${answer?.kind === "accepting"}
										.ariaLabel=${this.t("admin.invitations.declineAria", { name: workspaceName })}
										@click=${() => {
											this.confirming = true;
											this.answer = null;
										}}
										>${this.t("admin.invitations.decline")}</schmancy-button
									>
								</div>
							`,
						],
						[
							"confirming",
							() => html`
								<div class="flex flex-wrap items-center gap-2">
									<schmancy-button
										data-decline-confirm
										variant="outlined"
										color="error"
										size="sm"
										?busy=${answer?.kind === "declining"}
										.ariaLabel=${this.t("admin.invitations.declineAria", { name: workspaceName })}
										@click=${() => this.answerInvitation("decline")}
										>${this.t("admin.invitations.declineConfirm")}</schmancy-button
									>
									<schmancy-button
										data-keep
										variant="outlined"
										color="neutral"
										size="sm"
										?disabled=${answer?.kind === "declining"}
										.ariaLabel=${this.t("admin.invitations.keepAria", { name: workspaceName })}
										@click=${() => {
											this.confirming = false;
											this.answer = null;
										}}
										>${this.t("admin.invitations.keep")}</schmancy-button
									>
								</div>
							`,
						],
					])}
				</div>
			</schmancy-surface>
		`;
	}
}

declare global {
	interface HTMLElementTagNameMap {
		"invitation-row": InvitationRow;
	}
}
